import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getAccessoryPurchases, deleteAccessoryPurchase } from '../../actions/accessoryPurchase';
import { Search, Trash2, ShoppingCart, Settings, AlertTriangle, MapPin, Package, User, Calendar, Eye, Globe } from 'lucide-react';

const AccessoryPurchases = ({ getAccessoryPurchases, deleteAccessoryPurchase, accessoryPurchases, pagination, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [accessoryPurchaseToDelete, setAccessoryPurchaseToDelete] = useState(null);
    const [filteredAccessoryPurchases, setFilteredAccessoryPurchases] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);

    useEffect(() => {
        getAccessoryPurchases(currentPage, limit);
    }, [currentPage]);

    useEffect(() => {
        if (accessoryPurchases) {
            const filtered = accessoryPurchases.filter(accessoryPurchase =>
                accessoryPurchase.accessory?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                accessoryPurchase.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                accessoryPurchase.deliveryAddress?.fullAddress?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredAccessoryPurchases(filtered);
        }
    }, [accessoryPurchases, searchTerm]);

    const handleDeleteClick = (accessoryPurchase) => {
        setAccessoryPurchaseToDelete(accessoryPurchase);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (accessoryPurchaseToDelete) {
            deleteAccessoryPurchase(accessoryPurchaseToDelete._id);
            setShowDeleteModal(false);
            setAccessoryPurchaseToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setAccessoryPurchaseToDelete(null);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'processing': return 'warning';
            case 'out of delivery': return 'info';
            case 'delivered': return 'success';
            case 'refunded': return 'secondary';
            case 'canceled': return 'danger';
            default: return 'secondary';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'processing': return '⏳';
            case 'out of delivery': return '🚚';
            case 'delivered': return '✅';
            case 'refunded': return '💰';
            case 'canceled': return '❌';
            default: return '📦';
        }
    };

    const openInGoogleMaps = (coordinates) => {
        const googleMapsUrl = process.env.REACT_APP_GOOGLE_MAPS_URL || 'https://www.google.com/maps';
        const latitude = coordinates[0];
        const longitude = coordinates[1];
        const mapsUrl = `${googleMapsUrl}?q=${latitude},${longitude}`;
        window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRefresh = () => {
        getAccessoryPurchases(currentPage, limit);
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <ShoppingCart size={32} className="me-3 text-primary" />
                                    Accessory Purchases
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage accessory purchase orders and delivery tracking
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{pagination?.total || 0}</div>
                                        <div className="small">Total Purchases</div>
                                    </div>
                                    <Settings size={28} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{pagination?.pages || 0}</div>
                                        <div className="small">Total Pages</div>
                                    </div>
                                    <Package size={28} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{pagination?.page || 1}</div>
                                        <div className="small">Current Page</div>
                                    </div>
                                    <Calendar size={28} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{filteredAccessoryPurchases?.length || 0}</div>
                                        <div className="small">Showing</div>
                                    </div>
                                    <Eye size={28} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search Section */}
                <div className="row mb-4">
                    <div className="col-lg-8">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search by accessory, status, or address..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setSearchTerm('')}
                                            title="Clear search"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <button 
                                    onClick={handleRefresh}
                                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                                    disabled={loading}
                                >
                                    <Settings size={18} className="me-2" />
                                    {loading ? 'Refreshing...' : 'Refresh'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Accessory Purchases Table */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading accessory purchases...</p>
                    </div>
                ) : filteredAccessoryPurchases && filteredAccessoryPurchases.length > 0 ? (
                    <div className="card shadow-sm">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <Package size={16} className="me-2 text-muted" />
                                                    Accessory
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <ShoppingCart size={16} className="me-2 text-muted" />
                                                    Quantity
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <Settings size={16} className="me-2 text-muted" />
                                                    Status
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <MapPin size={16} className="me-2 text-muted" />
                                                    Delivery Address
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <Calendar size={16} className="me-2 text-muted" />
                                                    Created
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAccessoryPurchases.map((accessoryPurchase) => (
                                            <tr key={accessoryPurchase._id} className="align-middle">
                                                <td className="text-center">
                                                    <div className="fw-bold text-primary">
                                                        {accessoryPurchase.accessory?.name || 'Unknown Accessory'}
                                                    </div>
                                                    <div className="small text-muted">
                                                        ID: {accessoryPurchase._id.slice(-6)}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="fw-bold text-info">
                                                        {accessoryPurchase.quantity || 0}
                                                    </div>
                                                    <div className="small text-muted">Items</div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex flex-column align-items-center gap-1">
                                                        <span 
                                                            className={`badge bg-${getStatusColor(accessoryPurchase.status)}`}
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            {getStatusIcon(accessoryPurchase.status)} {accessoryPurchase.status || 'Unknown'}
                                                        </span>
                                                        {accessoryPurchase.delivered && (
                                                            <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>
                                                                Delivered
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {accessoryPurchase.deliveryAddress?.fullAddress ? (
                                                        <div>
                                                            <div className="small fw-bold" style={{ 
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                maxWidth: '200px'
                                                            }}>
                                                                {accessoryPurchase.deliveryAddress.fullAddress}
                                                            </div>
                                                            {accessoryPurchase.deliveryAddress?.location?.coordinates && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary mt-1"
                                                                    onClick={() => openInGoogleMaps(accessoryPurchase.deliveryAddress.location.coordinates)}
                                                                    style={{ fontSize: '0.7rem' }}
                                                                >
                                                                    <Globe size={12} className="me-1" />
                                                                    View Map
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">No address</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="fw-bold">
                                                        {new Date(accessoryPurchase.createdAt).toLocaleDateString()}
                                                    </div>
                                                    <div className="small text-muted">
                                                        {new Date(accessoryPurchase.createdAt).toLocaleTimeString()}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="btn-group" role="group">
                                                        <Link 
                                                            to={`/inventory/v1.2/accessory-purchases/${accessoryPurchase._id}`}
                                                            className="btn btn-sm btn-outline-primary"
                                                            title="View Details"
                                                        >
                                                            <Eye size={14} />
                                                        </Link>
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteClick(accessoryPurchase)}
                                                            title="Delete Purchase"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <ShoppingCart size={64} className="text-muted mb-3" />
                        <h4 className="text-muted mb-3">No Accessory Purchases Found</h4>
                        <p className="text-muted mb-4">
                            {searchTerm ? 'No purchases match your search criteria.' : 'Get started by adding your first accessory purchase.'}
                        </p>
                    </div>
                )}

                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="d-flex justify-content-center">
                                <nav aria-label="Accessory Purchases pagination">
                                    <ul className="pagination">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(1)}
                                                disabled={currentPage === 1}
                                            >
                                                ««
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                ‹
                                            </button>
                                        </li>
                                        
                                        {/* Page numbers */}
                                        {(() => {
                                            const pageNumbers = [];
                                            const startPage = Math.max(1, currentPage - 2);
                                            const endPage = Math.min(pagination.pages, currentPage + 2);
                                            
                                            for (let i = startPage; i <= endPage; i++) {
                                                pageNumbers.push(
                                                    <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => handlePageChange(i)}
                                                        >
                                                            {i}
                                                        </button>
                                                    </li>
                                                );
                                            }
                                            return pageNumbers;
                                        })()}
                                        
                                        <li className={`page-item ${currentPage === pagination.pages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === pagination.pages}
                                            >
                                                ›
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === pagination.pages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(pagination.pages)}
                                                disabled={currentPage === pagination.pages}
                                            >
                                                »»
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div 
                    className="modal fade show" 
                    style={{ 
                        display: 'block',
                        zIndex: 1050,
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        overflow: 'auto',
                        background: 'rgba(0,0,0,0.5)'
                    }} 
                    tabIndex="-1"
                >
                    <div className="modal-dialog modal-dialog-centered" style={{ pointerEvents: 'auto' }}>
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title text-danger">
                                    <AlertTriangle size={20} className="me-2" />
                                    Confirm Delete
                                </h5>
                                <button type="button" className="btn-close" onClick={cancelDelete}></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this accessory purchase?</p>
                                <p className="text-muted small mb-0">This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer border-0">
                                <button type="button" className="btn btn-secondary" onClick={cancelDelete}>
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger" 
                                    onClick={confirmDelete}
                                    style={{ position: 'relative', zIndex: 1055 }}
                                >
                                    <Trash2 size={16} className="me-2" />
                                    Delete Purchase
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

AccessoryPurchases.propTypes = {
    getAccessoryPurchases: PropTypes.func.isRequired,
    deleteAccessoryPurchase: PropTypes.func.isRequired,
    accessoryPurchases: PropTypes.array,
    pagination: PropTypes.object,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    accessoryPurchases: state.accessoryPurchase.accessoryPurchases,
    pagination: state.accessoryPurchase.pagination,
    loading: state.accessoryPurchase.loading
});

export default connect(mapStateToProps, { getAccessoryPurchases, deleteAccessoryPurchase })(AccessoryPurchases);
